import {
  VOWELS,
  createRng,
  type CharacterDef,
  type CharacterVoice,
  type Vowel,
} from '@archon3d/core';

/** Everything the voice synth needs to know about a character's throat. */
export interface VoiceProfile {
  /** Base pitch, Hz. */
  pitch: number;
  /** Size of the throat: formant frequencies are multiplied by this. */
  formantScale: number;
  /** 0 … 1 roughness. */
  growl: number;
  /** 0 … 1 breath noise. */
  breathiness: number;
  /** Vowels the character favours. */
  vowels: readonly Vowel[];
  /** Speed of utterances; 1 is normal. */
  tempo: number;
  /** Pitch wobble in cents: a trait of the voice, not of the utterance. */
  vibrato: number;
}

/** FNV-1a: tiny, stable across platforms, and it spreads similar ids far apart. */
export function hashString(text: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/**
 * The voice a character is born with: every parameter is drawn from a generator seeded with its
 * id, so the same id always speaks the same way and no two ids sound alike. Higher tiers are
 * bigger creatures – lower, slower and rougher.
 */
export function deriveVoiceProfile(id: string, tier = 1): VoiceProfile {
  const rng = createRng(hashString(id));
  const size = Math.max(0, Math.min(1, (tier - 1) / 4));
  // Log-uniform, so low and high voices are equally likely.
  const logRange = (lo: number, hi: number) =>
    Math.exp(Math.log(lo) + rng.next() * (Math.log(hi) - Math.log(lo)));
  const pitch = logRange(120, 420) / (1 + 0.8 * size);
  const formantScale = (0.85 + rng.next() * 0.4) * (1 - 0.22 * size);
  const growl = Math.min(1, rng.next() ** 2 * 0.7 + 0.3 * size);
  const breathiness = 0.05 + rng.next() * 0.45;
  const tempo = (0.85 + rng.next() * 0.45) * (1 - 0.25 * size);
  const vibrato = 4 + rng.next() * 30;
  const vowels = shuffle([...VOWELS], rng).slice(0, 2 + rng.int(2));
  return { pitch, formantScale, growl, breathiness, vowels, tempo, vibrato };
}

/** The derived voice with the character's own `voice` tuning laid over it. */
export function voiceProfileFor(
  character: Pick<CharacterDef, 'id' | 'tier' | 'voice'>,
): VoiceProfile {
  return applyVoiceOverrides(deriveVoiceProfile(character.id, character.tier), character.voice);
}

export function applyVoiceOverrides(profile: VoiceProfile, voice?: CharacterVoice): VoiceProfile {
  if (!voice) return profile;
  return {
    ...profile,
    pitch: voice.pitch ?? profile.pitch,
    formantScale: voice.formantScale ?? profile.formantScale,
    growl: voice.growl ?? profile.growl,
    breathiness: voice.breathiness ?? profile.breathiness,
    tempo: voice.tempo ?? profile.tempo,
    vowels: voice.vowels && voice.vowels.length > 0 ? [...voice.vowels] : profile.vowels,
  };
}

function shuffle<T>(items: T[], rng: { int(maxExclusive: number): number }): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [items[i], items[j]] = [items[j]!, items[i]!];
  }
  return items;
}
